import moment from 'moment';
import _ from 'lodash';

import { getUserDistrictId } from 'State/selectors';
import {
  requestAccess,
  resetAccessPrompt,
} from 'Common/Components/AccessPrompt/state/actions';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { createToast, toastType } from 'Common/ToastHelper';
import { Api } from 'API/MPWApi';
import getDatabase, { getDatabaseNames } from 'Common/DB';
import {
  SYNC_ACCESS_TOKEN,
  IS_PRODUCTION,
  DATE_FORMAT_FOR_DB_STRING,
  DATE_FORMAT_FOR_UI,
  SYNC_TIMEOUT_MINUTES,
} from 'Common/Constants';
import { token } from 'API/MPWApi';
import { ClosedStatusEnum } from 'Common/Constants/Enumeration/ClosedStatusEnum';
import { MealTypesEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import * as ConnectionActions from 'State/connection/actions';
import * as ConnectionSelectors from 'State/connection/selectors';

import { retrieveClosedDatesInOfflineDB } from './helpers';
import {
  isValidTransaction,
  buildTransactionRequest,
} from 'PointOfSale/helpers/transactions';

export const syncOfflineTransactionHistory = (
  showConsoleMessages = false
) => async (dispatch, getState, MPWWebAPI) => {
  if (ConnectionSelectors.getOfflineSyncInProgress(getState())) {
    return;
  }

  dispatch(ConnectionActions.setOfflineSyncInProgress(true));
  // First, sync offline transactions.
  await dispatch(transactionHistorySync(showConsoleMessages));
  // Next, if there are any transactions from closed days handle those.
  await dispatch(handleOfflineClosedDateTransactions());
  dispatch(ConnectionActions.setOfflineSyncInProgress(false));
};

const handleOfflineClosedDateTransactions = () => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  const state = getState();

  const districtId = getUserDistrictId(state);
  const closedDates = await retrieveClosedDatesInOfflineDB(districtId);

  if (closedDates.length < 1) {
    return;
  }

  /** Do one at a time */
  const closedDate = closedDates[0];
  const closedDateUiFormat = moment(closedDate.ClosingDate).format(
    DATE_FORMAT_FOR_UI
  );
  const SiteId = closedDate.PrimarySiteId;
  const IsSupper = closedDate.Supper;

  const WriteUncloseDaysModel = {
    BeginDT: closedDateUiFormat,
    EndDT: closedDateUiFormat,
    IsSupper,
    SiteId,
    mealSummaries: [],
  };

  const siteDateString = `${
    IsSupper ? 'Supper' : 'B/L/O'
  } on ${closedDateUiFormat} at site ${SiteId}`;

  const actionsToDispatchOnOk = [
    async () => {
      try {
        await MPWWebAPI.postGenericInsertUpdate(
          `uncloseday/WriteUncloseDays`,
          WriteUncloseDaysModel
        );
        createToast(
          toastType.success,
          'Success',
          `${siteDateString} unclosed successfully!`
        );
        await dispatch(transactionHistorySync());
        dispatch(handleOfflineClosedDateTransactions());
      } catch (error) {
        if (error.response.status === 401) {
          dispatch(
            requestAccess({
              showCancel: false,
              /** We may get a user that is unauthorized, don't assume that we should close */
              closeOnUser: false,
              onUser: async user => {
                try {
                  const UserApi = new Api(user.Token);
                  await UserApi.postGenericInsertUpdate(
                    `uncloseday/WriteUncloseDays`,
                    WriteUncloseDaysModel
                  );
                  createToast(
                    toastType.success,
                    'Success',
                    `${siteDateString} unclosed successfully!`
                  );

                  /** Close access prompt, because at this point the API was successful */
                  dispatch(resetAccessPrompt());
                  /** Start syncing transactions that had previously closed days */
                  await dispatch(transactionHistorySync());
                  /** Start process all over again to see if there are any other unclosed transactions left */
                  dispatch(handleOfflineClosedDateTransactions());
                } catch (e) {
                  createToast(
                    toastType.error,
                    'Error',
                    `You do not have access to unclose the day.`
                  );
                }
              },
              title: `Please enter credentials to unclose day for ${siteDateString}`,
            })
          );
        }
      }
    },
  ];

  const dialog = {
    title: `There are offline transactions for ${siteDateString}`,
    message:
      'The day will be unclosed and offline transactions will be processed.',
    actionsToDispatchOnOk,
    showCancel: false,
  };

  dispatch(setNotificationDialog(dialog));
};

/**
 * Sync transactions saved in the browser's database.
 * It will only sync databases from the same district as the logged in user.
 * @param {boolean} showConsoleMessages - Will optionally output the processing status to the console
 */
const transactionHistorySync = (showConsoleMessages = false) => async (
  dispatch,
  getState,
  MPWWebAPI
) => {
  try {
    showConsoleMessages = showConsoleMessages || !IS_PRODUCTION;
    const districtId = getUserDistrictId(getState());

    showConsoleMessages &&
      console.info('----- Starting TransactionHistory sync -----');

    if (!token.isValid()) {
      showConsoleMessages &&
        console.info(`User session is expired, please login`);
      throw new Error('User session is expired');
    }

    const databaseNames = await getDatabaseNames();

    const ERRORED_DATABASES = [];
    const syncTime = moment().unix();
    const closed = {};
    const open = {};

    for (const databaseName of databaseNames) {
      if (showConsoleMessages) {
        console.groupEnd(); // end the group here as the previous iteration may have continued at multiple points.
        console.group(`Attempting to sync ${databaseName}`);
      }

      const DB = await getDatabase(databaseName);
      const { userInfo } = (await DB.AccessToken.get(SYNC_ACCESS_TOKEN)) || {};

      /* Only attempt to sync databases that are from the same district as the logged in user. */
      const dbDistrictId = _.get(userInfo, 'districtId', '');
      if (dbDistrictId !== districtId) {
        showConsoleMessages &&
          console.info(`DistrictId missmatch or missing, continue sync`);
        continue;
      }

      const transactions = await DB.TransactionHistory.toArray();
      /**
       * No Transactions, continue on to next database sync
       */
      if (transactions.length === 0) {
        showConsoleMessages &&
          console.info(`No TransactionHistory, continue sync`);
        continue;
      }

      /**
       * If sync is in progress or failed and the timeout hasn't been passed, continue on to next database sync
       */
      const syncStatus = await DB.SyncStatus.toCollection().last();
      if (
        syncStatus &&
        !syncStatus.EndAt &&
        moment(syncStatus.StartAt)
          .add(SYNC_TIMEOUT_MINUTES, 'minutes')
          .isAfter()
      ) {
        showConsoleMessages &&
          console.info(
            `A previous sync attempt is in progress or the failure timout hasn't passed, continue sync`
          );
        continue;
      } else {
        await DB.SyncStatus.put({
          StartAt: syncTime,
          EndAt: undefined,
        });
      }

      showConsoleMessages &&
        console.info(`Syncing ${transactions.length} TransactionHistory`);

      /**
       * Send transactions
       */
      const processedTransactionIds = [];
      const erroredTransactions = [];
      const closedDateTransactions = [];

      for (let transaction of transactions) {
        const mealType =
          transaction.MealType === MealTypesEnum.Supper ? 'S' : 'BLO';

        const path = `${transaction.PrimarySiteId}.${mealType}`;

        if (!_.has(open, path)) {
          _.set(open, path, new Set());
        }

        if (!_.has(closed, path)) {
          _.set(closed, path, new Set());
        }

        const closingDate = moment(transaction.WorkingDT).format(
          DATE_FORMAT_FOR_DB_STRING
        );

        /** If transaction's date is closed, keep it and move on */
        if (_.invoke(open, `${path}.has`, closingDate)) {
        } else {
          try {
            if (_.invoke(closed, `${path}.has`, closingDate)) {
              throw new Error(`${closingDate} is Closed`);
            }
            /** Check if transaction has closed date if not in  */
            const body = {
              PrimarySiteId: transaction.PrimarySiteId,
              ClosingDate: closingDate,
              Supper: transaction.MealType === MealTypesEnum.Supper,
            };
            const response = await MPWWebAPI.postGenericInsertUpdate(
              'Serving/GetClosedStatus',
              body
            );
            if (response.status !== 200) {
              throw new Error(response.statusText);
            }
            const [Status] = response.data.Data;
            if (Status && Status.ClosedStatus === ClosedStatusEnum.CLOSED) {
              throw new Error(`${closingDate} is Closed`);
            } else {
              _.invoke(open, `${path}.add`, closingDate);
            }
          } catch (e) {
            _.invoke(closed, `${path}.add`, closingDate);
            closedDateTransactions.push(transaction);
            continue;
          }
        }

        try {
          //Since some transactions won't have a GUID, as they existed before we started using client created GUIDs, we need to update them.
          if (!isValidTransaction(transaction)) {
            transaction = buildTransactionRequest(transaction);
            await DB.TransactionHistory.update(transaction.Id, transaction);
          }

          const response = await MPWWebAPI.postGenericInsertUpdate(
            'Serving/Transaction',
            transaction
          );

          if (response.status !== 200) {
            const error = new Error(response.statusText);
            // NOTE: Cannot set error.response = response, error.response stringifies to "{}"
            error.response = {
              ok: response.ok,
              text: await response.text(),
              status: response.status,
            };
            throw error;
          }
          /** add to list of processed ids to remove after loop */
          processedTransactionIds.push(transaction.Id);
        } catch (error) {
          erroredTransactions.push({
            TransactionHistoryId: transaction.Id,
            CreatedAt: new Date(),
            Error: JSON.stringify(error),
          });
        }
      }

      if (showConsoleMessages) {
        console.info('Closed', Object.entries(closed));
        console.info('Open', Object.entries(open));

        console.info(
          `Synced: ${processedTransactionIds.length} of ${transactions.length} TransactionHistory`
        );
        console.info(
          `Errored: ${erroredTransactions.length} of ${transactions.length} TransactionHistory`
        );
        console.info(
          `Closed: ${closedDateTransactions.length} of ${transactions.length} TransactionHistory`
        );

        for (const [primarySiteId, datesByMealType] of Object.entries(closed)) {
          for (const [mealType, dates] of Object.entries(datesByMealType)) {
            const dateArray = [...dates];
            for (const date of dateArray) {
              const closedDateTransactionsForDate = closedDateTransactions.filter(
                t =>
                  primarySiteId === t.PrimarySiteId &&
                  date ===
                    moment(t.WorkingDT).format(DATE_FORMAT_FOR_DB_STRING) &&
                  mealType.includes(t.MealType)
              );

              console.info(
                `Closed ${mealType} on ${moment(date).format(
                  DATE_FORMAT_FOR_UI
                )} at site ${primarySiteId}, has ${
                  closedDateTransactionsForDate.length
                }`
              );
            }
          }
        }
      }

      if (erroredTransactions.length) {
        ERRORED_DATABASES.push(databaseName);
      }
      // remove processed transactions
      await DB.TransactionHistory.bulkDelete(processedTransactionIds);
      // remove processed transactions that had previous errored
      await DB.TransactionHistoryErrorLogs.where('TransactionHistoryId')
        .anyOf(processedTransactionIds)
        .delete();
      // add errored transactions to the error log table
      await DB.TransactionHistoryErrorLogs.bulkAdd(erroredTransactions);

      // Clean out SyncStatus' that are older than a week.
      await DB.SyncStatus.where('StartAt')
        .below(
          moment()
            .subtract(1, 'weeks')
            .unix()
        )
        .delete();

      await DB.SyncStatus.put({
        StartAt: syncTime,
        EndAt: moment().unix(),
      });

      if (ERRORED_DATABASES.length) {
        throw new Error('Some transactions failed to sync.');
      }
    }
    console.groupEnd();
  } catch (error) {
    return Promise.reject(error.message);
  }
};



// WEBPACK FOOTER //
// ./src/offline/actions.js